import { RegulationComparisonEnum, RegulationComparisonLabelEnum } from 'submodules/nerit-aquatec-sdk/services/regulation/enums/RegulationComparisonEnum'
import { FieldRegulationConditionTP } from 'submodules/nerit-aquatec-sdk/services/regulation/types/FieldRegulationConditionTP'

/** Linha da formatação condicional no modal, ainda em edição (campos podem estar vazios). */
export type ConditionRowTP = {
    comparison?: RegulationComparisonEnum
    value?: number
    valueTo?: number
    limit?: number
}

/**
 * Faixas do limite condicional na tela: texto das faixas, legenda, linhas do modal.
 * A validação de sobreposição e de buracos é da API; aqui só obrigatoriedade.
 */
export class RegulationConditionUtils {

    static readonly COMPARISON_OPTIONS = Object.values(RegulationComparisonEnum).map(comparison => ({
        value: comparison,
        label: RegulationComparisonLabelEnum[comparison],
    }))

    /** Número com vírgula decimal e ao menos uma casa: 8 → "8,0", 7.5 → "7,5". */
    static formatNumber(value: number | string): string {
        return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 6 })
    }

    /** Texto da faixa. Ex.: "pH ≤ 7,5", "7,5 < pH ≤ 8,0", "pH > 8,5". */
    static describe(condition: FieldRegulationConditionTP, referenceName: string): string {

        const value = RegulationConditionUtils.formatNumber(condition.value)

        switch (condition.comparison) {
            case RegulationComparisonEnum.LESS_OR_EQUAL:
                return `${referenceName} ≤ ${value}`
            case RegulationComparisonEnum.LESS:
                return `${referenceName} < ${value}`
            case RegulationComparisonEnum.GREATER_OR_EQUAL:
                return `${referenceName} ≥ ${value}`
            case RegulationComparisonEnum.GREATER:
                return `${referenceName} > ${value}`
            case RegulationComparisonEnum.EQUAL:
                return `${referenceName} = ${value}`
            case RegulationComparisonEnum.BETWEEN:
                return `${value} < ${referenceName} ≤ ${RegulationConditionUtils.formatNumber(condition.valueTo!)}`
            default:
                return ''
        }
    }

    /** Ex.: "pH ≤ 7,5 | limite 3,7". */
    static legendText(condition: FieldRegulationConditionTP, referenceName: string): string {
        return `${RegulationConditionUtils.describe(condition, referenceName)} | limite ${RegulationConditionUtils.formatNumber(condition.limit)}`
    }

    /** Ex.: "Condicional (pH)". */
    static conditionalLabel(referenceName: string): string {
        return `Condicional (${referenceName})`
    }

    /** Tooltip de um valor de norma condicional. Ex.: "Limite aplicado: 1,0 (pH 8,3)". */
    static appliedLimitText(value: { appliedMaxPermitted?: number | null, referenceValue?: number | null, isLimitNotApplicable?: boolean }, referenceName: string): string {

        if (value.isLimitNotApplicable || value.appliedMaxPermitted === null || value.appliedMaxPermitted === undefined)
            return `Sem limite aplicável: amostra sem ${referenceName}`

        const reference = value.referenceValue === null || value.referenceValue === undefined ? '' : ` ${RegulationConditionUtils.formatNumber(value.referenceValue)}`
        return `Limite aplicado: ${RegulationConditionUtils.formatNumber(value.appliedMaxPermitted)} (${referenceName}${reference})`
    }

    static legendLines(conditions: FieldRegulationConditionTP[], referenceName: string): string[] {
        return conditions.map(condition => RegulationConditionUtils.legendText(condition, referenceName))
    }

    static emptyRow(): ConditionRowTP {
        return {}
    }

    static isBetween(row: ConditionRowTP): boolean {
        return row.comparison === RegulationComparisonEnum.BETWEEN
    }

    /** Linha com tipo, valor(es) e limite numéricos. "Entre" exige os dois valores. */
    static isRowComplete(row: ConditionRowTP): boolean {
        const isNumber = (value?: number): boolean => value !== undefined && value !== null && !Number.isNaN(value)
        return !!row.comparison && isNumber(row.value) && isNumber(row.limit) && (!RegulationConditionUtils.isBetween(row) || isNumber(row.valueTo))
    }

    /** Faixas para salvar, na ordem das linhas; `valueTo` só em "Entre". */
    static toConditions(rows: ConditionRowTP[]): FieldRegulationConditionTP[] {
        return rows.map(row => ({
            comparison: row.comparison!,
            value: row.value!,
            valueTo: RegulationConditionUtils.isBetween(row) ? row.valueTo : undefined,
            limit: row.limit!,
        }))
    }

    static fromConditions(conditions?: FieldRegulationConditionTP[]): ConditionRowTP[] {
        return (conditions ?? []).map(condition => ({ ...condition }))
    }

    /**
     * Número para o texto do campo, com vírgula decimal: 7.5 → "7,5".
     * O campo só reaproveita o valor recebido quando ele muda; em texto, "7," e "7" não brigam com o que está sendo digitado.
     */
    static toInputText(value?: number): string | undefined {
        return value === undefined || value === null || Number.isNaN(value) ? undefined : `${value}`.replace('.', ',')
    }

    /** Valor digitado ("7,5", "7.5", 7.5) para número; vazio vira undefined. */
    static parseNumber(input?: string | number | null): number | undefined {
        if (input === undefined || input === null || `${input}`.trim() === '')
            return undefined

        const value = Number(`${input}`.replace(',', '.'))
        return Number.isNaN(value) ? undefined : value
    }

    /**
     * Faixa no eixo do parâmetro de referência. Lados abertos vão até a borda do eixo.
     * "Igual a" é uma linha (`value`), não uma faixa.
     */
    static axisBand(condition: FieldRegulationConditionTP, axis: { min: number, max: number }): { from: number, to: number } | { value: number } {

        const value = Number(condition.value)

        switch (condition.comparison) {
            case RegulationComparisonEnum.LESS_OR_EQUAL:
            case RegulationComparisonEnum.LESS:
                return { from: axis.min, to: value }
            case RegulationComparisonEnum.GREATER_OR_EQUAL:
            case RegulationComparisonEnum.GREATER:
                return { from: value, to: axis.max }
            case RegulationComparisonEnum.BETWEEN:
                return { from: value, to: Number(condition.valueTo) }
            default:
                return { value }
        }
    }
}
